import type {
  FetchTariffListParams,
  Tariff,
  UpdateTariffRequestData
} from 'Services/ProductManagement/Features/Tariff/types'
import type { ListResponse } from 'Services/ProductManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  TARIFF = 'Tariff'
}

export const tariffApi = createBaseApi({
  reducerPath: 'tariffApi',
  keepUnusedDataFor: 5,
  tagTypes: [TagTypes.TARIFF]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getTariffs: builder.query<ListResponse<Tariff>, FetchTariffListParams | void>({
      query: params => ({
        url: '/api/v1/tariff/',
        params: params ?? undefined
      }),
      providesTags: result => {
        if (result) {
          return [
            ...result.resources.map(({ id }) => ({
              type: TagTypes.TARIFF,
              id
            })),
            { type: TagTypes.TARIFF, id: RTKCacheTags.LIST }
          ]
        }
        return [{ type: TagTypes.TARIFF, id: RTKCacheTags.LIST }]
      }
    }),
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getAllTariffs: builder.query<ListResponse<Tariff>, FetchTariffListParams | void>({
      query: params => ({
        url: '/api/v1/tariff/',
        params: params ?? undefined
      }),
      providesTags: result => {
        if (result) {
          return [
            ...result.resources.map(({ id }) => ({
              type: TagTypes.TARIFF,
              id
            })),
            { type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }
          ]
        }
        return [{ type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }]
      }
    }),
    getSingleTariff: builder.query<Tariff, string>({
      query: id => `/api/v1/tariff/${id}/`,
      providesTags: (_result, _error, id) => [{ type: TagTypes.TARIFF, id }]
    }),
    createSingleTariff: builder.mutation<Tariff, Partial<Tariff>>({
      query: data => ({
        url: `/api/v1/tariff/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    updateSingleTariff: builder.mutation<Tariff, { id: string } & UpdateTariffRequestData>({
      query: data => {
        const { id, ...fields } = data
        return {
          url: `/api/v1/tariff/${id}/`,
          method: 'PUT',
          body: fields
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagTypes.TARIFF, id },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    deleteSingleTariff: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/tariff/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: TagTypes.TARIFF, id },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    copySingleTariff: builder.mutation<Tariff, { id: string }>({
      query: data => ({
        url: `/api/v1/tariff/${data.id}/copy/`,
        method: 'POST'
      }),
      invalidatesTags: [
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    importSingleTariff: builder.mutation<Record<string, never>, { id: string }>({
      query: data => ({
        url: `/api/v1/tariff/${data.id}/import/`,
        method: 'POST'
      }),
      invalidatesTags: [
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }
      ]
    }),
    deleteTariffProduct: builder.mutation<
      Record<string, never>,
      { tariffId: string; productId: string }
    >({
      query: ({ tariffId, productId }) => ({
        url: `/api/v1/tariff/${tariffId}/product/${productId}/`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { tariffId }) => [
        { type: TagTypes.TARIFF, id: tariffId },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST },
        { type: TagTypes.TARIFF, id: RTKCacheTags.LIST_ALL }
      ]
    })
  })
})

export const {
  useGetTariffsQuery,
  useGetAllTariffsQuery,
  useGetSingleTariffQuery,
  useCreateSingleTariffMutation,
  useUpdateSingleTariffMutation,
  useDeleteSingleTariffMutation,
  useCopySingleTariffMutation,
  useImportSingleTariffMutation,
  useDeleteTariffProductMutation
} = tariffApi
